// London and San Francisco do not share a clock. These match the backend
// office zones: the last day is a calendar date in the employee's office,
// and the move happens at midnight at the start of the next day there.
const OFFICES = {
  UK: { label: 'London', timeZone: 'Europe/London' },
  US: { label: 'San Francisco', timeZone: 'America/Los_Angeles' },
  // Perth is the last Australian clock to reach midnight. The employee
  // record stores the country, not a city, so this does not end an
  // Australian last day while it is still evening further west.
  AUS: { label: 'Australia', timeZone: 'Australia/Perth' },
};

export function officeForCountry(country) {
  const code = String(country || '').trim().toUpperCase();
  if (['UK', 'GB', 'UNITED KINGDOM', 'GREAT BRITAIN', 'ENGLAND', 'SCOTLAND', 'WALES', 'NORTHERN IRELAND'].includes(code)) {
    return OFFICES.UK;
  }
  if (['AUS', 'AU', 'AUSTRALIA'].includes(code)) {
    return OFFICES.AUS;
  }
  return OFFICES.US;
}

export function officeToday(timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const value = (type) => parts.find((part) => part.type === type)?.value;
    return `${value('year')}-${value('month')}-${value('day')}`;
  } catch (e) {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }
}

export function formatCalendarDate(iso) {
  if (!iso) return 'Not recorded';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!match) return String(iso);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatInstant(iso, timeZone) {
  if (!iso) return 'Not recorded';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch (e) {
    return String(iso);
  }
}

export function formatDismissalText(report) {
  if (!report) return '';
  const name = (report.employee?.name || report.employee?.email || 'Employee').toUpperCase();
  const lines = [name];

  const fmtShort = (iso) => {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderPolicy = (policy) => {
    if (!policy.year_history || policy.year_history.length === 0) return;
    if (policy.year_history.length > 1) lines.push('');

    policy.year_history.forEach((yr, idx) => {
      const start = fmtShort(yr.period_start);
      const end = fmtShort(yr.period_end);
      const startYear = yr.period_start.slice(0, 4);
      const endYear = yr.period_end.slice(0, 4);
      let desc = '';
      if (yr.is_first && yr.is_last) desc = ', only year pro-rated';
      else if (yr.is_first) desc = ', first year pro-rated';
      else if (yr.is_last) desc = ', last year pro-rated';
      const yearLabel = startYear === endYear ? startYear : `${startYear}/${endYear}`;
      lines.push(`${yearLabel} (${start} - ${end}${desc})`);
      lines.push('');

      if (idx > 0) {
        lines.push(`Carried forward: ${yr.carried_in}`);
      }

      if (yr.upfront_days != null) {
        lines.push(`Full ${yr.upfront_days} days upfront (available to book)`);
      }

      const dpy = yr.days_per_year;
      lines.push(`Accrual: (${yr.days_in_period}/365) \u00d7 ${dpy} = ${yr.accrual} days`);

      if (yr.requests && yr.requests.length > 0) {
        const parts = yr.requests.map(r => r.days % 1 === 0 ? String(r.days | 0) : String(r.days));
        lines.push(`Used: ${parts.join(' + ')} = ${yr.used} days`);
      } else {
        lines.push(`Used: 0`);
      }

      if (!yr.is_last) {
        const sign = yr.year_end_balance >= 0 ? '+' : '';
        const note = yr.carryover_note || '';
        lines.push(`Year-end balance: ${sign}${yr.year_end_balance} -- ${note}`);
        lines.push(`Carries into next year: ${yr.carried_into_next}`);
      } else {
        const sign = yr.year_end_balance >= 0 ? '+' : '';
        lines.push(`Balance at dismissal: ${sign}${yr.year_end_balance} days`);
      }

      if (idx < policy.year_history.length - 1) lines.push('');
    });

    if (policy.adjustments !== 0) {
      lines.push('');
      lines.push(`Manual adjustments: ${policy.adjustments > 0 ? '+' : ''}${policy.adjustments} days`);
    }
  };

  (report.pto?.policies || []).forEach((policy) => {
    lines.push('');
    if ((report.pto?.policies || []).length > 1) {
      lines.push(`--- ${policy.policy_name} ---`);
    }
    renderPolicy(policy);
  });

  if ((report.sick?.policies || []).length > 0) {
    lines.push('');
    lines.push('SICK TIME');
    report.sick.policies.forEach((policy) => {
      if (report.sick.policies.length > 1) {
        lines.push('');
        lines.push(`--- ${policy.policy_name} ---`);
      }
      renderPolicy(policy);
    });
  }

  lines.push('');
  lines.push('---');
  if ((report.pto?.policies || []).length > 0) {
    const net = report.pto.total_net_balance;
    const sign = net >= 0 ? '+' : '';
    lines.push(`PTO net balance: ${sign}${net} days`);
    if (net < 0) lines.push(`(${Math.abs(net)} days owed back at dismissal)`);
  }
  if ((report.sick?.policies || []).length > 0) {
    lines.push(`Sick net balance: ${report.sick.total_net_balance} days`);
  }

  return lines.join('\n');
}

export function ptoBalanceText(dismissal) {
  const net = dismissal?.report?.pto?.total_net_balance;
  if (net === undefined || net === null) return 'Not recorded';
  const sign = net > 0 ? '+' : '';
  return `${sign}${net} days`;
}
